import React, { createContext, useContext, useMemo, useState } from "react";

// Day/Night display mode for the School chrome. Night mode paints the navbar in
// the footer colour and flips its content (icons + text) to white. Night is the
// default for everyone: the footer switcher was removed while day mode is on
// hold. Day mode and its `setNight` plumbing are kept intact (nothing here was
// deleted) so re-enabling it later is just restoring the switcher - and day can
// still be previewed by setting this localStorage key to "0". The choice
// persists across navigations so it survives SPA route changes and full reloads.
const STORAGE_KEY = "v2_school_night_mode";

const NightModeContext = createContext({ night: true, setNight: () => {} });

function readInitial() {
  try {
    // Only an explicit "0" opts out of night; unset (the common case) and "1"
    // both resolve to night, so everyone lands on night by default.
    return window.localStorage.getItem(STORAGE_KEY) !== "0";
  } catch (e) {
    // ignore storage errors (private mode, etc.)
    return true;
  }
}

// `onChange` mirrors FixedSidebarProvider: legacy pages mount the navbar and
// footer as separate React roots that cannot share live state, so they pass a
// reload callback here. After persisting the choice, a reload re-runs each root
// which reads the new value from localStorage at mount. The SPA (single root)
// omits onChange and updates live.
export function NightModeProvider({ children, onChange }) {
  const [night, setNightState] = useState(readInitial);

  const setNight = (next) => {
    setNightState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch (e) {
      // ignore storage errors (private mode, etc.)
    }
    onChange?.(next);
  };

  const value = useMemo(() => ({ night, setNight }), [night]);

  return (
    <NightModeContext.Provider value={value}>
      {children}
    </NightModeContext.Provider>
  );
}

export function useNightMode() {
  return useContext(NightModeContext);
}
